import React, {
  ReactElement,
  RefObject,
  useEffect,
  useRef,
  useState
} from 'react'
import { FA_CHEVRON_DOWN } from 'const/icons'
import { AccordionProps } from './typing'
import { ArrowIcon, Content, Header, HeaderButton, Item } from './subcomponents'

const NON_ACTIVE_INDEX = -1

/** Accordion component with open and close item capabilities */
const Accordion = ({ accordionItems }: AccordionProps): ReactElement => {
  // set to NON_ACTIVE_INDEX to close the current opened item
  const [activeItemIndex, setActiveItemIndex] = useState(NON_ACTIVE_INDEX)

  // saving a ref to every item to scroll to it when opened
  const itemRefs = useRef<RefObject<HTMLHeadingElement>[]>(
    Array.from({ length: accordionItems.length }, () => React.createRef())
  )

  const activateItemOnClick = (index: number) => {
    if (index === activeItemIndex) {
      setActiveItemIndex(NON_ACTIVE_INDEX)
    } else {
      setActiveItemIndex(index)
    }
  }

  useEffect(() => {
    if (activeItemIndex >= 0) {
      itemRefs.current[activeItemIndex].current.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }, [activeItemIndex, itemRefs])

  return (
    <div>
      {accordionItems.map((item, index) => {
        const isItemActive = activeItemIndex === index

        const itemId = `accordion-${item.itemId}`
        const itemHeaderId = `${itemId}-header-button`
        const itemContentId = `${itemId}-item-content`

        return (
          <Item ref={itemRefs.current[index]} key={itemId}>
            <Header>
              <HeaderButton
                id={itemHeaderId}
                aria-expanded={isItemActive}
                aria-controls={itemContentId}
                buttonType="text"
                onClick={() => activateItemOnClick(index)}
              >
                {item.header}
                <ArrowIcon
                  $isItemActive={isItemActive}
                  name={FA_CHEVRON_DOWN}
                />
              </HeaderButton>
            </Header>

            {isItemActive && (
              <Content id={itemContentId} aria-labelledby={itemHeaderId}>
                {item.content}
              </Content>
            )}
          </Item>
        )
      })}
    </div>
  )
}

export default Accordion
